import { useState } from "react";
import { Package } from "lucide-react";

/**
 * Product thumbnail with graceful fallback.
 * If `image_url` is missing or fails to load → renders a gray tile with
 * the `Package` icon and (optionally) the first letter of `name`.
 *
 *  <ProductThumbnail image_url={p.image_url} name={p.name} size="md" />
 *
 * Sizes:
 *   xs  → 32×32   (dense tables / row previews)
 *   sm  → 44×44
 *   md  → 64×64   (default, catalog style)
 *   lg  → 88×88
 */
const SIZE_CLASSES = {
  xs: "w-8 h-8 text-xs",
  sm: "w-11 h-11 text-sm",
  md: "w-16 h-16 text-base",
  lg: "w-[88px] h-[88px] text-lg",
};

export const ProductThumbnail = ({
  image_url,
  name = "",
  size = "md",
  className = "",
  rounded = "rounded-lg",
}) => {
  const [failed, setFailed] = useState(false);
  const sizeCls = SIZE_CLASSES[size] || SIZE_CLASSES.md;
  const initial = (name || "").trim().charAt(0).toUpperCase();

  const showImage = image_url && !failed;

  return (
    <div
      className={`${sizeCls} ${rounded} flex-shrink-0 flex items-center justify-center overflow-hidden bg-[#F3F4F6] border border-[#E5E7EB] ${className}`}
      title={name}
      data-testid="product-thumbnail"
    >
      {showImage ? (
        <img
          src={image_url}
          alt={name}
          className="w-full h-full object-cover"
          onError={() => setFailed(true)}
          loading="lazy"
        />
      ) : (
        <div className="relative flex items-center justify-center w-full h-full text-[#9CA3AF]">
          <Package className="w-1/2 h-1/2" strokeWidth={1.5} />
          {initial && (
            <span className="absolute bottom-0.5 right-1 font-semibold text-[#6B7280]/70 leading-none">
              {initial}
            </span>
          )}
        </div>
      )}
    </div>
  );
};

export default ProductThumbnail;
